import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Receipt from './pages/Receipt/Receipt';
import EditHourmeter from './pages/EditHourmeter/EditHourmeter';
import CreateHourmeter from './pages/CreateHourmeter/CreateHourmeter';
import CreateFuelLoad from './pages/CreateFuelLoad/CreateFuelLoad';
import FuelLoads from './pages/FuelLoads/FuelLoads';
import DumperTripDetail from './pages/DumperHourmeters/DumperTripDetail';
import RegisterDumperTrip from './pages/RegisterDumperTrip/RegisterDumperTrip';
import RegisterDumperTripQR from './pages/RegisterDumperTripQR/RegisterDumperTripQR';
import SelectAsset from './pages/SelectAsset/SelectAsset';
import RegisterDumperFuel from './pages/RegisterDumperFuel/RegisterDumperFuel';
import DumperQrGenerator from './pages/DumperQrGenerator/DumperQrGenerator';
import Loading from './components/ui/Loading';

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading size="lg" text="Verificando sesión..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve current path + search en ?redirect= para volver post-login
    const target = location.pathname + location.search;
    const redirectQuery = target && target !== '/'
      ? `?redirect=${encodeURIComponent(target)}`
      : '';
    return <Navigate to={`/login${redirectQuery}`} replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading size="lg" text="Verificando sesión..." />
      </div>
    );
  }

  if (isAuthenticated) {
    // Si vino con ?redirect=... (deep link interrumpido por login), respetar destino
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    return <Navigate to={redirect && redirect.startsWith('/') ? redirect : '/dashboard'} replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/receipt/:id"
        element={
          <ProtectedRoute>
            <Receipt />
          </ProtectedRoute>
        }
      />

      <Route
        path="/edit-hourmeter/:id"
        element={
          <ProtectedRoute>
            <EditHourmeter />
          </ProtectedRoute>
        }
      />

      <Route
        path="/create-hourmeter"
        element={
          <ProtectedRoute>
            <CreateHourmeter />
          </ProtectedRoute>
        }
      />

      <Route
        path="/create-fuel-load"
        element={
          <ProtectedRoute>
            <CreateFuelLoad />
          </ProtectedRoute>
        }
      />

      <Route
        path="/fuel-loads"
        element={
          <ProtectedRoute>
            <FuelLoads />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-hourmeters/qr"
        element={
          <ProtectedRoute>
            <RegisterDumperTripQR />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-hourmeters/register"
        element={
          <ProtectedRoute>
            <RegisterDumperTrip />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-hourmeters/select-asset"
        element={
          <ProtectedRoute>
            <SelectAsset />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-hourmeters/qr/generate"
        element={
          <ProtectedRoute>
            <DumperQrGenerator />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-fuel/register"
        element={
          <ProtectedRoute>
            <RegisterDumperFuel />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dumper-hourmeters/:id"
        element={
          <ProtectedRoute>
            <DumperTripDetail />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
